'use client'

import { useEffect, useRef } from 'react'
import { Heading } from '@/components/Heading'
import Input from '@/components/Input'
import { Select } from '@/components/Select'
import { Box } from '@/components/Box'
import { Button } from '@/components/Button'
import { Question } from '@/components/Question'
import { occupationOptions, employmentTypeOptions, paymentFrequencyOptions, otherIncomeDescriptionOptions } from '@/data/selectOptions'
import { MinusCircleIcon } from '@heroicons/react/24/outline'
import Textarea from '@/components/Textarea'
import { ROUTES } from '@/config/routes'
import useFormContext from '@/context/useFormContext'
import { useForm, useFieldArray } from 'react-hook-form'
import { Header } from '@/components/HeaderPersonal'
import { generateYearsOptions } from '@/utils/dateUtils'
import usePersistedNavigation from '@/hooks/usePersistedNavigation'
import { useFormValidation } from '@/context/FormValidationContext'
import { useFormSubmission } from '@/hooks/useFormSubmission'
import { useFormSaveForLater } from '@/hooks/useFormSaveForLater'
import { preparePayload } from '@/services/saveService'
import { scrollToFirstError } from '@/utils/helper'
import { validateMoney } from '@/utils/validation'

const yearsInJobOptions = generateYearsOptions(true)

function Income () {
  const navigate = usePersistedNavigation()
  const { state, dispatch } = useFormContext()
  const { register, unregister, watch, control, formState: { errors, isSubmitted }, handleSubmit, setValue, getValues, trigger } = useForm({
    defaultValues: {
      companyName: state.personal.income.companyName,
      occupation: state.personal.income.occupation,
      employmentType: state.personal.income.employmentType,
      timeInThisJobYears: state.personal.income.timeInThisJobYears,
      takeHomePayFrequency: state.personal.income.takeHomePayFrequency,
      incomeAmount: state.personal.income.incomeAmount,
      spousePartnerIncome: state.personal.income.spousePartnerIncome,
      spousePartnerFrequency: state.personal.income.spousePartnerFrequency,
      otherIncome: state.personal.income.otherIncome,
      otherIncomes: state.personal.income.otherIncomes, // Array of objects
      previousCompanyName: state.personal.income.previousCompanyName,
      previousOccupation: state.personal.income.previousOccupation,
      previousEmploymentType: state.personal.income.previousEmploymentType,
      previousTimeInThisJobYears: state.personal.income.previousTimeInThisJobYears,
      coBorrowerCompanyName: state.personal.income.coBorrowerCompanyName,
      coBorrowerOccupation: state.personal.income.coBorrowerOccupation,
      coBorrowerEmploymentType: state.personal.income.coBorrowerEmploymentType,
      coBorrowerTimeInThisJobYears: state.personal.income.coBorrowerTimeInThisJobYears,
      coBorrowerOtherIncome: state.personal.income.coBorrowerOtherIncome,
      coBorrowerPreviousCompanyName: state.personal.income.coBorrowerPreviousCompanyName,
      coBorrowerPreviousOccupation: state.personal.income.coBorrowerPreviousOccupation,
      coBorrowerPreviousEmploymentType: state.personal.income.coBorrowerPreviousEmploymentType,
      coBorrowerPreviousTimeInThisJobYears: state.personal.income.coBorrowerPreviousTimeInThisJobYears,
      incomeDecreaseExpected: state.personal.income.incomeDecreaseExpected,
      incomeDecreaseDetails: state.personal.income.incomeDecreaseDetails
    }
  })

  // We want to track state in ref for use in the triggerSave function which is passed as a callback on the mount
  const stateRef = useRef(state)
  useEffect(() => { stateRef.current = state }, [state])

  const { handleFormSave } = useFormSubmission({
    onSuccess: (data) => {
      console.log('Form submission successful:', data)
    },
    onError: (error) => {
      console.error('Error during form save:', error)
    }
  })

  const dispatchFormData = (data: any) => {
    console.log('Form dispatch:', data)
    dispatch({
      type: 'UPDATE_PERSONAL',
      step: 'income',
      payload: data.personal.income
    })
  }

  const isJointApplication = state.personal.aboutYou.isJoint === true

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'otherIncomes' // Name of the array in the form
  })

  const { registerValidationTrigger, unregisterValidationTrigger } = useFormValidation()
  const triggerFormValidation = () => handleSubmit(() => {})()

  // Register the validation trigger when the component mounts
  useEffect(() => {
    registerValidationTrigger(triggerFormValidation)
    return () => unregisterValidationTrigger()
  }, [registerValidationTrigger, unregisterValidationTrigger])

  /* Handle saving for later */
  const { registerSaveCallback, unregisterSaveCallback } = useFormSaveForLater()
  const triggerSave = () => {
    const incomeData = getValues()
    const data = bindIncomeData(incomeData)

    dispatchFormData(data)
    handleFormSave(preparePayload(data, 'personal', 'income'))
  }

  // Register the save form trigger when the component mounts
  useEffect(() => {
    registerSaveCallback(triggerSave)
    return () => unregisterSaveCallback()
  }, [registerSaveCallback, unregisterSaveCallback])
  /* End - Handle saving for later */

  // Register all custom select components https://react-hook-form.com/faqs#Whatifyoudonthaveaccesstoref
  useEffect(() => {
    register('occupation', { required: 'Occupation is required' })
    register('employmentType', { required: 'Employment type is required' })
    register('timeInThisJobYears', { required: 'Time in this job (years) is required' })
    register('takeHomePayFrequency', { required: 'Take home pay frequency is required' })
    register('otherIncome', {
      validate: (value) => value === true || value === false || 'This field is required'
    })

    // Handle co-borrower fields based on joint application
    if (isJointApplication) {
      register('spousePartnerFrequency', { required: 'Take home pay frequency is required' })
      register('coBorrowerOccupation', { required: 'Occupation is required' })
      register('coBorrowerEmploymentType', { required: 'Employment type is required' })
      register('coBorrowerTimeInThisJobYears', { required: 'Time in this job (years) is required' })
      register('coBorrowerOtherIncome', {
        validate: (value) => value === true || value === false || 'This field is required'
      })
    } else {
      unregister('spousePartnerFrequency')
      unregister('coBorrowerOccupation')
      unregister('coBorrowerEmploymentType')
      unregister('coBorrowerTimeInThisJobYears')
      unregister('coBorrowerOtherIncome')
    }

    // Handle previous employment fields based on time in job
    const timeInJobYears = Number(watch('timeInThisJobYears'))
    if (timeInJobYears < 3) {
      register('previousOccupation', { required: 'Occupation is required' })
      register('previousEmploymentType', { required: 'Employment type is required' })
      register('previousTimeInThisJobYears', { required: 'Time in this job (years) is required' })
    } else {
      unregister('previousOccupation')
      unregister('previousEmploymentType')
      unregister('previousTimeInThisJobYears')
    }

    // Handle co-borrower previous employment fields based on time in job
    if (isJointApplication) {
      const coBorrowerTimeInJobYears = Number(watch('coBorrowerTimeInThisJobYears'))
      if (coBorrowerTimeInJobYears < 3) {
        register('coBorrowerPreviousOccupation', { required: 'Occupation required' })
        register('coBorrowerPreviousEmploymentType', { required: 'Employment type required' })
        register('coBorrowerPreviousTimeInThisJobYears', { required: 'Time in this job (years) required' })
      } else {
        unregister('coBorrowerPreviousOccupation')
        unregister('coBorrowerPreviousEmploymentType')
        unregister('coBorrowerPreviousTimeInThisJobYears')
      }
    }

    register('incomeDecreaseExpected', { 
      validate: (value) => value === true || value === false || 'This field is required'
    })
  }, [isJointApplication, watch('timeInThisJobYears'), watch('coBorrowerTimeInThisJobYears')])

  const bindIncomeData = (data: any) => {
    const currentState = stateRef.current

    return {
      ...currentState,
      business: null,
      personal: {
        ...currentState.personal,
        income: {
          ...currentState.personal.income,
          companyName: data.companyName,
          occupation: data.occupation,
          employmentType: data.employmentType,
          timeInThisJobYears: data.timeInThisJobYears,
          takeHomePayFrequency: data.takeHomePayFrequency,
          incomeAmount: data.incomeAmount,
          spousePartnerIncome: data.spousePartnerIncome === '' ? null : data.spousePartnerIncome,
          spousePartnerFrequency: data.spousePartnerFrequency,
          otherIncome: data.otherIncome,
          otherIncomes: data.otherIncomes,
          previousCompanyName: data.previousCompanyName,
          previousOccupation: data.previousOccupation,
          previousEmploymentType: data.previousEmploymentType,
          previousTimeInThisJobYears: data.previousTimeInThisJobYears,
          coBorrowerCompanyName: data.coBorrowerCompanyName,
          coBorrowerOccupation: data.coBorrowerOccupation,
          coBorrowerEmploymentType: data.coBorrowerEmploymentType,
          coBorrowerTimeInThisJobYears: data.coBorrowerTimeInThisJobYears,
          coBorrowerOtherIncome: data.coBorrowerOtherIncome,
          coBorrowerPreviousCompanyName: data.coBorrowerPreviousCompanyName,
          coBorrowerPreviousOccupation: data.coBorrowerPreviousOccupation,
          coBorrowerPreviousEmploymentType: data.coBorrowerPreviousEmploymentType,
          coBorrowerPreviousTimeInThisJobYears: data.coBorrowerPreviousTimeInThisJobYears,
          incomeDecreaseExpected: data.incomeDecreaseExpected,
          incomeDecreaseDetails: data.incomeDecreaseDetails
        }
      }
    }
  }

  const onSubmit = (data: any) => {
    data = bindIncomeData(data)
    dispatchFormData(data)
    handleFormSave(preparePayload(data, 'personal', 'income'), ROUTES.PERSONAL.FINANCIAL_POSITION.path)
  }

  // Handle our custom select component
  const handleSelectChange = (fieldName: string, value: string) => {
    // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
    setValue(fieldName, value)
    // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
    if (isSubmitted) trigger(fieldName)
  }

  const appendAndRegisterOtherIncome = (isBorrower: boolean = false) => {
    const index = fields.length
    append({ amount: '', frequency: '', incomeType: '', isBorrower })
    register(`otherIncomes.${index}.frequency`, { required: 'Frequency is required' })
    register(`otherIncomes.${index}.incomeType`, { required: 'Description is required' })
  }

  const removeAndUnregisterOtherIncome = (fieldID: string) => {
    const index = fields.findIndex(field => field.id === fieldID)
    unregister(`otherIncomes.${index}.frequency`)
    unregister(`otherIncomes.${index}.incomeType`)
    remove(index)
  }

  const showOtherIncomes = watch('otherIncome') === true
  const showCoBorrowerOtherIncomes = watch('coBorrowerOtherIncome') === true
  const showPreviousEmployer = watch('timeInThisJobYears') < 3
  const showCoBorrowerPreviousEmployer = watch('coBorrowerTimeInThisJobYears') < 3
  const showIncomeDecreaseDetails = watch('incomeDecreaseExpected') === true
  if (showOtherIncomes) {
    // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
    const mainApplicantIncomes = fields.filter(field => !field.isBorrower)
    if (mainApplicantIncomes.length === 0) appendAndRegisterOtherIncome()
  } 

  if (showCoBorrowerOtherIncomes) {
    // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
    const coBorrowerIncomes = fields.filter(field => field.isBorrower)
    if (coBorrowerIncomes.length === 0) appendAndRegisterOtherIncome(true)
  }

  useEffect(() => {
    if (!showOtherIncomes) {
      // To completely remove all fields from useFieldArray in React Hook Form, the remove function without any arguments can be used. This will remove all items within the field array.
      // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
      const mainApplicantIncomes = fields.filter(field => !field.isBorrower)
      const mainApplicantIncomesIDs = mainApplicantIncomes.map(field => field.id)
      mainApplicantIncomesIDs.forEach(id => {
        // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
        remove(id)
      })
    }
    
    if (!showCoBorrowerOtherIncomes) {
      // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
      const coBorrowerIncomes = fields.filter(field => field.isBorrower)
      const coBorrowerIncomesIDs = coBorrowerIncomes.map(field => field.id)
      coBorrowerIncomesIDs.forEach(id => {
        // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
        remove(id)
      })
    }
  }, [showOtherIncomes, showCoBorrowerOtherIncomes])

  console.log('errors', errors)

  return (
    <>
      <Header />
      <form onSubmit={async (e) => {
        handleSubmit(onSubmit)(e)

        // Add this line to scroll to first error if there are any
        if (Object.keys(errors).length > 0) {
          scrollToFirstError()
        }
      }}>
        <section>
          <Heading>Employer information</Heading>
          <Box>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8'>
              <Input
                label='Employer name'
                inputId='employerName'
                required={true}
                placeholder='Employer name'
                {...register('companyName', { required: 'Employer name is required' })}
                error={errors.companyName}
              />
              <Select
                label='Occupation'
                required={true}
                selectOptions={occupationOptions}
                defaultValue={state.personal.income.occupation}
                onChange={value => handleSelectChange('occupation', value)}
                error={errors.occupation}
              />
              <Select
                label='Employment type'
                required={true}
                selectOptions={employmentTypeOptions}
                defaultValue={state.personal.income.employmentType}
                onChange={value => handleSelectChange('employmentType', value)}
                error={errors.employmentType}
              />
            </div>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8'>
              <Select
                label='Time in this job'
                required={true}
                selectOptions={yearsInJobOptions}
                defaultValue={state.personal.income.timeInThisJobYears}
                onChange={value => handleSelectChange('timeInThisJobYears', value)}
                error={errors.timeInThisJobYears}
              />
            </div>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-7 md:gap-8'>
              <Select
                label='How often do you get paid?'
                required={true}
                selectOptions={paymentFrequencyOptions}
                defaultValue={state.personal.income.takeHomePayFrequency}
                onChange={value => handleSelectChange('takeHomePayFrequency', value)}
                error={errors.takeHomePayFrequency}
              />
              <Input
                label='Income (take home pay)' 
                placeholder='Amount' 
                inputId='incomeAmount'
                required={true}
                money={true}
                {...register('incomeAmount', { 
                  required: 'Income is required',
                  validate: (value) => validateMoney(true, value)
                })}
                error={errors.incomeAmount}
              />
            </div>
            {/* If it's not a joint application, we still ask it as optional, if it's joint this will be asked in the coborrower section */}
            { !isJointApplication && (
              <div className='grid grid-cols-1 md:grid-cols-2 gap-7 md:gap-8'>
                <Select
                  label='How often does your partner get paid?'
                  selectOptions={paymentFrequencyOptions}
                  defaultValue={state.personal.income.spousePartnerFrequency}
                  onChange={value => handleSelectChange('spousePartnerFrequency', value)}
                  error={errors.spousePartnerFrequency}
                />
                <Input
                  label='Your Spouse/Partner income (take home pay)' 
                  placeholder='Amount' 
                  inputId='spousePartnerIncome'
                  money={true}
                  {...register('spousePartnerIncome', {
                    validate: (value) => validateMoney(false, value)
                  })}
                  error={errors.spousePartnerIncome}
                />
              </div>
            )}
            <Question
              title='Do you receive other income? *'
              name='otherIncome'
              defaultValue={state.personal.income.otherIncome}
              onChange={value => handleSelectChange('otherIncome', value)}
              error={errors.otherIncome}
              options={[
                { id: 'otherIncomeYes', title: 'Yes', value: true },
                { id: 'otherIncomeNo', title: 'No', value: false }
              ]}
            />

            { showOtherIncomes && (
              <>
                {fields
                  // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
                  .filter(field => !field.isBorrower)
                  .map((field, index) => {
                    const realIndex = fields.findIndex(f => f.id === field.id)
                    return (
                      <div key={field.id} className='relative grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8 pb-12'>
                        <Input
                          label='Amount'
                          placeholder='Amount'
                          inputId={`otherIncomes[${realIndex}].amount`}
                          required={true}
                          money={true}
                          {...register(`otherIncomes.${realIndex}.amount`, {
                            required: 'Amount is required',
                            validate: (value) => validateMoney(true, value)
                          })}
                          // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
                          error={errors.otherIncomes?.[realIndex]?.amount}
                        />
                        <Select
                          label='Frequency'
                          required={true}
                          selectOptions={paymentFrequencyOptions}
                          defaultValue={state.personal.income.otherIncomes[realIndex]?.frequency}
                          onChange={value => handleSelectChange(`otherIncomes.${realIndex}.frequency`, value)}
                          // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
                          error={errors.otherIncomes?.[realIndex]?.frequency}
                        />
                        <Select
                          label='Description'
                          required
                          selectOptions={otherIncomeDescriptionOptions}
                          defaultValue={state.personal.income.otherIncomes[realIndex]?.incomeType}
                          onChange={value => handleSelectChange(`otherIncomes.${realIndex}.incomeType`, value)}
                          // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
                          error={errors.otherIncomes?.[realIndex]?.incomeType}
                        />
                        {index > 0 && (
                          <button
                            type='button'
                            className='cursor-pointer transition duration-300 flex items-center justify-center rounded-full absolute z-10 bottom-0 right-0 p-2 hover:bg-[var(--theme-bg)] hover:text-[var(--brand-1)]'
                            onClick={() => removeAndUnregisterOtherIncome(field.id)}
                          >                      
                            <MinusCircleIcon className='w-6 h-6' />
                          </button>
                        )}
                      </div>
                    )
                  })
                }
                {fields.length < 18 && (
                  <div className='flex justify-end'>
                    <Button icon="add" style='text' onClick={() => appendAndRegisterOtherIncome()}>
                      Add another income
                    </Button>
                  </div>
                )}
              </>
            )}
          </Box>

          { showPreviousEmployer && (
            <>
              <Heading as='h2'>Previous employer details</Heading>
              <Box>
                <div className='grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8'>
                  <Input
                    label='Employer name'
                    inputId='previousCompanyName'
                    required={true}
                    placeholder='Employer name' 
                    {...register('previousCompanyName', { required: 'Employer name is required' })}
                    error={errors.previousCompanyName} 
                  />
                  <Select
                    label='Occupation'
                    required={true}
                    selectOptions={occupationOptions}
                    defaultValue={state.personal.income.previousOccupation}
                    onChange={value => handleSelectChange('previousOccupation', value)}
                    error={errors.previousOccupation}
                  />
                  <Select
                    label='Employment type'
                    required={true}
                    selectOptions={employmentTypeOptions}
                    defaultValue={state.personal.income.previousEmploymentType}
                    onChange={value => handleSelectChange('previousEmploymentType', value)}
                    error={errors.previousEmploymentType}
                  />
                </div>
                <div className='grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8'>
                  <Select
                    label='Time in this job'
                    required={true}
                    selectOptions={yearsInJobOptions}
                    defaultValue={state.personal.income.previousTimeInThisJobYears}
                    onChange={value => handleSelectChange('previousTimeInThisJobYears', value)}
                    error={errors.previousTimeInThisJobYears}
                  />
                </div>
              </Box>
            </>
          )}

          { isJointApplication && 
          <>
            <Heading as='h2'>Co-borrower employer information</Heading>
            <Box>
              <div className='grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8'>
                <Input
                  label='Employer name'
                  inputId='coBorrowerCompanyName'
                  required={true}
                  placeholder='Employer name'
                  {...register('coBorrowerCompanyName', { required: 'Employer name is required' })}
                  error={errors.coBorrowerCompanyName}
                />
                <Select
                  label='Occupation'
                  required={true}
                  selectOptions={occupationOptions}
                  defaultValue={state.personal.income.coBorrowerOccupation}
                  onChange={value => handleSelectChange('coBorrowerOccupation', value)}
                  error={errors.coBorrowerOccupation}
                />
                <Select
                  label='Employment type'
                  required={true}
                  selectOptions={employmentTypeOptions}
                  defaultValue={state.personal.income.coBorrowerEmploymentType}
                  onChange={value => handleSelectChange('coBorrowerEmploymentType', value)}
                  error={errors.coBorrowerEmploymentType}
                />
              </div>
              <div className='grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8'>
                <Select
                  label='Time in this job'
                  required={true}
                  selectOptions={yearsInJobOptions}
                  defaultValue={state.personal.income.coBorrowerTimeInThisJobYears}
                  onChange={value => handleSelectChange('coBorrowerTimeInThisJobYears', value)}
                  error={errors.coBorrowerTimeInThisJobYears}
                />
              </div>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-7 md:gap-8'>
                <Select
                  label='How often do you get paid?'
                  required={true}
                  selectOptions={paymentFrequencyOptions}
                  defaultValue={state.personal.income.spousePartnerFrequency}
                  onChange={value => handleSelectChange('spousePartnerFrequency', value)}
                  error={errors.spousePartnerFrequency}
                />
                <Input
                  label='Spouse/Partner income' 
                  placeholder='Amount' 
                  inputId='spousePartnerIncome'
                  required={true}
                  money={true}
                  {...register('spousePartnerIncome', {
                    required: 'Income is required',
                    validate: (value) => validateMoney(true, value)
                  })}
                  error={errors.spousePartnerIncome}
                />
              </div>
              <Question
                title='Do you receive other income? *'
                name='coBorrowerOtherIncome'
                defaultValue={state.personal.income.coBorrowerOtherIncome}
                onChange={value => handleSelectChange('coBorrowerOtherIncome', value)}
                error={errors.coBorrowerOtherIncome}
                options={[
                  { id: 'coBorrowerOtherIncomeYes', title: 'Yes', value: true },
                  { id: 'coBorrowerOtherIncomeNo', title: 'No', value: false }
                ]}
              />

              { showCoBorrowerOtherIncomes && (
                <>
                  {fields
                    // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
                    .filter(field => field.isBorrower)
                    .map((field, index) => {
                      const realIndex = fields.findIndex(f => f.id === field.id)
                      return (
                        <div key={field.id} className='relative grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8 pb-12'>
                          <Input
                            label='Amount'
                            placeholder='Amount'
                            inputId={`otherIncomes[${realIndex}].amount`}
                            {...register(`otherIncomes.${realIndex}.amount`, {
                              required: 'Amount is required',
                              validate: (value) => validateMoney(true, value)
                            })}
                            // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
                            error={errors.otherIncomes?.[realIndex]?.amount}
                          />
                          <Select
                            label='Frequency'
                            required={true}
                            selectOptions={paymentFrequencyOptions}
                            defaultValue={state.personal.income.otherIncomes[realIndex]?.frequency}
                            onChange={value => handleSelectChange(`otherIncomes.${realIndex}.frequency`, value)}
                            // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
                            error={errors.otherIncomes?.[realIndex]?.frequency}
                          />
                          <Select
                            label='Description'
                            required
                            selectOptions={otherIncomeDescriptionOptions}
                            defaultValue={state.personal.income.otherIncomes[realIndex]?.incomeType}
                            onChange={value => handleSelectChange(`otherIncomes.${realIndex}.incomeType`, value)}
                            // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
                            error={errors.otherIncomes?.[realIndex]?.incomeType}
                          />
                          {index > 0 && (
                            <button
                              type='button'
                              className='cursor-pointer transition duration-300 flex items-center justify-center rounded-full absolute z-10 bottom-0 right-0 p-2 hover:bg-[var(--theme-bg)] hover:text-[var(--brand-1)]'
                              onClick={() => removeAndUnregisterOtherIncome(field.id)}
                            >                      
                              <MinusCircleIcon className='w-6 h-6' />
                            </button>
                          )}
                        </div>
                      )
                    })
                  }
                  {fields.length < 18 && (
                    <div className='flex justify-end'>
                      <Button icon="add" style='text' onClick={() => appendAndRegisterOtherIncome(true)}>
                        Add another income
                      </Button>
                    </div>
                  )}
                </>
              )}
            </Box>

            { showCoBorrowerPreviousEmployer && (
              <>
                <Heading as='h2'>Co-borrower previous employer details</Heading>
                <Box>
                  <div className='grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8'>
                    <Input
                      label='Employer name'
                      inputId='coBorrowerPreviousCompanyName'
                      required={true}
                      placeholder='Employer name' 
                      {...register('coBorrowerPreviousCompanyName', { required: 'Employer name is required' })}
                      error={errors.coBorrowerPreviousCompanyName} 
                    />
                    <Select
                      label='Occupation'
                      required={true}
                      selectOptions={occupationOptions}
                      defaultValue={state.personal.income.coBorrowerPreviousOccupation}
                      onChange={value => handleSelectChange('coBorrowerPreviousOccupation', value)}
                      error={errors.coBorrowerPreviousOccupation}
                    />
                    <Select
                      label='Employment type'
                      required={true}
                      selectOptions={employmentTypeOptions}
                      defaultValue={state.personal.income.coBorrowerPreviousEmploymentType}
                      onChange={value => handleSelectChange('coBorrowerPreviousEmploymentType', value)}
                      error={errors.coBorrowerPreviousEmploymentType}
                    />
                  </div>
                  <div className='grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8'>
                    <Select
                      label='Time in this job'
                      required={true}
                      selectOptions={yearsInJobOptions}
                      defaultValue={state.personal.income.coBorrowerPreviousTimeInThisJobYears}
                      onChange={value => handleSelectChange('coBorrowerPreviousTimeInThisJobYears', value)}
                      error={errors.coBorrowerPreviousTimeInThisJobYears}
                    />
                  </div>
                </Box>
              </>
            )}
          </>
          }

          <Box>
            <Question
              title={isJointApplication ? 
                'Is your income or your spouse/partner\'s income likely to decrease over the next 12 months?' :
                'Is your income likely to decrease over the next 12 months?'
              }
              name='incomeDecreaseExpected'
              defaultValue={state.personal.income.incomeDecreaseExpected}
              onChange={value => handleSelectChange('incomeDecreaseExpected', value)}
              error={errors.incomeDecreaseExpected}
              options={[
                { id: 'incomeDecreaseExpectedYes', title: 'Yes', value: true },
                { id: 'incomeDecreaseExpectedNo', title: 'No', value: false }
              ]}
            />
            { showIncomeDecreaseDetails &&
              <div>
                <Textarea
                  label='Give us the details of why your income may decrease'
                  inputId='incomeDecreaseDetails'
                  required={true}
                  placeholder='Give us the details of why your income may decrease'
                  {...register('incomeDecreaseDetails', { required: 'Details are required' })}
                  error={errors.incomeDecreaseDetails}
                />
              </div>
            }
          </Box>
          {state.isPostCall && (
            <p className="text-center text-gray-600 mt-6 max-w-2xl mx-auto">By clicking "Next" you are confirming that the above information is correct</p>
          )}
          <div className='flex justify-between mt-8 pb-16 max-w-5xl mx-auto'>
            <Button iconPosition='left' onClick={() => navigate(ROUTES.PERSONAL.ABOUT_YOU.path)}>Back</Button>
            <Button type='submit'>Next</Button>
          </div>
        </section>
      </form>
    </>
  )
}

export default Income
